import { cardMatchesMovie, type MovieTheme } from '@/lib/movies';

export interface AutoDeckCard {
  name: string;
  card_type?: string | null;
  ink_color?: string | null;
  cost?: number | null;
  inkable?: boolean | null;
  subtitle?: string | null;
  classifications?: string[] | null;
  id: string;
}

export interface AutoDeckEntry {
  cardId: string;
  card: AutoDeckCard;
  quantity: number;
  fromMovie: boolean;
}

/** Target composition for a balanced 60-card deck. */
export const AUTO_DECK_TARGETS: Record<string, number> = {
  Character: 34,
  Action: 8,
  Song: 6,
  Item: 6,
  Location: 6,
};

const DECK_SIZE = 60;

/** Prefer cheap-to-mid, inkable cards so the auto deck has a playable curve. */
function score(c: AutoDeckCard): number {
  const cost = typeof c.cost === 'number' ? c.cost : 4;
  const curve = cost <= 1 ? 1 : cost <= 3 ? 4 : cost <= 5 ? 3 : cost <= 7 ? 2 : 0.5;
  return curve + (c.inkable ? 1.5 : 0);
}

function typeOf(c: AutoDeckCard): string {
  return c.card_type || 'Character';
}

/**
 * Build a balanced movie-themed decklist: characters, actions/songs, items and
 * locations from the chosen film first, then same-ink filler to reach 60 cards.
 */
export function buildMovieDeck(
  allCards: AutoDeckCard[],
  theme: MovieTheme | undefined,
  ink1: string,
  ink2: string,
): AutoDeckEntry[] {
  const inkLegal = allCards.filter(c => c.ink_color === ink1 || c.ink_color === ink2);
  const movieIds = new Set(
    theme ? inkLegal.filter(c => cardMatchesMovie(c as any, theme)).map(c => c.id) : [],
  );

  const picked = new Map<string, AutoDeckEntry>();
  let total = 0;

  const take = (pool: AutoDeckCard[], limit: number) => {
    const sorted = [...pool].sort((a, b) => score(b) - score(a));
    for (const c of sorted) {
      if (limit <= 0 || total >= DECK_SIZE) return;
      const existing = picked.get(c.id);
      const room = Math.min(4 - (existing?.quantity ?? 0), limit, DECK_SIZE - total);
      if (room <= 0) continue;
      const qty = Math.min(room, 4);
      picked.set(c.id, {
        cardId: c.id,
        card: c,
        quantity: (existing?.quantity ?? 0) + qty,
        fromMovie: movieIds.has(c.id),
      });
      limit -= qty;
      total += qty;
    }
  };

  const byType = (pool: AutoDeckCard[], type: string) => pool.filter(c => typeOf(c) === type);
  const moviePool = inkLegal.filter(c => movieIds.has(c.id));
  const fillerPool = inkLegal.filter(c => !movieIds.has(c.id));

  // Movie-first, per type, then top up each type from the rest of the ink pair.
  for (const [type, target] of Object.entries(AUTO_DECK_TARGETS)) {
    const before = total;
    take(byType(moviePool, type), target);
    const stillNeeded = target - (total - before);
    if (stillNeeded > 0) take(byType(fillerPool, type), stillNeeded);
  }

  // Anything left over (rare types missing from the pool) fills with best cards.
  if (total < DECK_SIZE) take(moviePool, DECK_SIZE - total);
  if (total < DECK_SIZE) take(fillerPool, DECK_SIZE - total);

  return [...picked.values()];
}
